/**
 * @typedef {"Product"|"Remanufacturing service"|string} MarketplaceListingClass
 * @typedef {"pending_provider"|"active"|"declined"|"completed"|"cancelled"|string} MarketplaceOrderStatus
 *
 * @typedef {Object} MarketplaceOrder
 * @property {string} id
 * @property {string} [productId]
 * @property {string} [productName]
 * @property {string} [productImageUrl]
 * @property {MarketplaceListingClass} [listingClass]
 * @property {"checkout"|"interest"|string} [source]
 * @property {"auto_confirm"|"provider_confirm"|string} [confirmationMode]
 * @property {string} [customerKeycloakId]
 * @property {string} [customerDisplayName]
 * @property {string} [providerKeycloakId]
 * @property {string} [providerDisplayName]
 * @property {MarketplaceOrderStatus} [status]
 * @property {string} [message]
 * @property {string} [providerNote]
 * @property {number|null} [quantity]
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 * @property {{stage?: string, stages?: Array<{key: string, label: string, completedAt?: string|null}>}} [productFulfillment]
 * @property {{milestones?: Array<{
 *   id?: string|number,
 *   key?: string,
 *   title?: string,
 *   status?: string,
 *   sortOrder?: number,
 *   inputs?: Record<string, unknown>
 * }>}} [serviceMilestones]
 * @property {Record<string, {
 *   meta?: { schemaVersion?: string, generatedAt?: string },
 *   object?: Record<string, Record<string, { value?: string, meta?: Record<string, unknown> }>>
 * }>} [digitalProductPassport]
 * @property {import("./serviceOrderWorkflow").ServiceWorkflow} [serviceWorkflow]
 * @property {Record<string, unknown>} [serviceDigitalProductPassport]
 */

export const MARKETPLACE_ORDER_STATUS_LABELS = {
  pending_provider: "Pending provider",
  active: "Active",
  declined: "Declined",
  completed: "Completed",
  cancelled: "Cancelled",
};

/** @typedef {"customer"|"provider"} MarketplaceOrderStatusPerspective */

export const MARKETPLACE_ORDER_INTEREST_PENDING_LABELS = {
  customer: "In Review",
  provider: "Pending Confirmation",
};

/**
 * @param {string|undefined|null} status
 * @param {{ source?: string, perspective?: MarketplaceOrderStatusPerspective }} [options]
 */
export function formatMarketplaceOrderStatus(status, options = {}) {
  if (!status || typeof status !== "string") return "Unknown";

  const { source, perspective } = options;
  if (
    status === "pending_provider" &&
    source === "interest" &&
    perspective &&
    MARKETPLACE_ORDER_INTEREST_PENDING_LABELS[perspective]
  ) {
    return MARKETPLACE_ORDER_INTEREST_PENDING_LABELS[perspective];
  }

  return (
    MARKETPLACE_ORDER_STATUS_LABELS[status] ??
    status.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

import { isRemanufacturingServiceClass } from "@helpers/productListingClass";

export function isServiceListing(order) {
  return isRemanufacturingServiceClass(order?.listingClass);
}

export function isProductListing(order) {
  return !isServiceListing(order);
}

const TERMINAL_ORDER_STATUSES = new Set(["declined", "completed", "cancelled"]);

/** Provider-declined interests are removed from buyer and seller dashboards. */
const DASHBOARD_HIDDEN_ORDER_STATUSES = new Set(["declined"]);

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function isCancelledInterestOrder(order) {
  return order?.source === "interest" && order?.status === "cancelled";
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 * @param {MarketplaceOrderStatusPerspective} perspective
 */
export function isCancelledInterestDismissedFromDashboard(order, perspective) {
  if (!isCancelledInterestOrder(order) || !perspective) {
    return false;
  }

  if (perspective === "customer") {
    return Boolean(
      order.dashboardDismissedByCustomerAt ?? order.dashboard_dismissed_by_customer_at
    );
  }

  return Boolean(
    order.dashboardDismissedByProviderAt ?? order.dashboard_dismissed_by_provider_at
  );
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 * @param {MarketplaceOrderStatusPerspective} [perspective]
 */
export function isVisibleInOrderDashboard(order, perspective) {
  if (!order?.status) return true;
  if (DASHBOARD_HIDDEN_ORDER_STATUSES.has(order.status)) return false;
  if (isCancelledInterestDismissedFromDashboard(order, perspective)) {
    return false;
  }
  return true;
}

/** Cancelled express-of-interest rows may be removed from the viewer's dashboard. */
export function canDismissCancelledInterestFromDashboard(order) {
  return isCancelledInterestOrder(order);
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} orders
 * @param {MarketplaceOrderStatusPerspective} [perspective]
 * @returns {Array<Record<string, unknown>>}
 */
export function filterOrdersForDashboard(orders, perspective) {
  if (!Array.isArray(orders)) return [];
  return orders.filter((order) => isVisibleInOrderDashboard(order, perspective));
}

/** Customer may withdraw an express-of-interest while it is still open. */
export function canCustomerCancelInterest(order) {
  if (order?.source !== "interest") return false;
  if (!order?.status || TERMINAL_ORDER_STATUSES.has(order.status)) return false;
  return order.status === "pending_provider" || order.status === "active";
}

const ACCEPTED_INTEREST_STATUSES = new Set(["active", "completed"]);

const OPEN_INTEREST_STATUSES = new Set(["pending_provider", "active"]);

/**
 * Whether the customer already has a non-terminal express-of-interest for this product.
 * @param {Array<{ productId?: string|number, source?: string, status?: string }>|null|undefined} orders
 * @param {string|number|null|undefined} productId
 */
function matchesOpenProductInterest(order, productId) {
  if (String(order?.productId ?? "") !== String(productId)) return false;
  if (order?.source !== "interest") return false;
  return OPEN_INTEREST_STATUSES.has(String(order?.status ?? ""));
}

export function hasOpenProductInterestForProduct(orders, productId) {
  if (productId == null || productId === "") return false;
  if (!Array.isArray(orders)) return false;
  return orders.some((order) => matchesOpenProductInterest(order, productId));
}

/**
 * Returns the customer's open express-of-interest order for a product, if any.
 * @param {Array<{ productId?: string|number, source?: string, status?: string }>|null|undefined} orders
 * @param {string|number|null|undefined} productId
 * @returns {Record<string, unknown>|null}
 */
export function findOpenProductInterestForProduct(orders, productId) {
  if (productId == null || productId === "") return null;
  if (!Array.isArray(orders)) return null;

  return (
    orders.find((order) => matchesOpenProductInterest(order, productId)) ?? null
  );
}

/**
 * Whether the customer has an accepted express-of-interest for this product.
 * @param {Array<{ productId?: string|number, source?: string, status?: string }>|null|undefined} orders
 * @param {string|number|null|undefined} productId
 */
export function hasAcceptedProductInterestForProduct(orders, productId) {
  if (productId == null || productId === "") return false;
  if (!Array.isArray(orders)) return false;

  const targetId = String(productId);
  return orders.some((order) => {
    if (String(order?.productId ?? "") !== targetId) return false;
    if (order?.source !== "interest") return false;
    return ACCEPTED_INTEREST_STATUSES.has(String(order?.status ?? ""));
  });
}
